"use client";

import { useCustomer } from "@/contexts/customer-context";
import { cn, getSymbol } from "@/lib/util/utils";
import { addToCart } from "@/modules/cart/actions/cart";
import { Button } from "@/modules/common/components/button";
import { QuantitySelector } from "@/modules/common/components/quantity-selector";
import { useFavorites } from "@/modules/favorites/hooks/use-favorites";
import { useCart } from "@/src/contexts/cart-context";
import {
  useCurrencyStore,
  useLocationStore,
  useToastStore,
} from "@/store/index";
import { canAddToCart, maxAddableQuantity } from "@/lib/util/stock";
import { StoreProduct } from "@medusajs/types";
import { useMutation } from "@tanstack/react-query";
import { Heart, ShoppingCart, Truck, X } from "lucide-react";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { useState } from "react";
import { subscribeToProduct } from "../actions/products";
import { useReviews } from "../hooks/use-reviews";
import ProductRating from "./product-rating";
import SubscribeToStockModal from "./subscribe-to-stock-modal";
import { StoreFavorite } from "@/modules/favorites/models/store-favorite";

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: StoreProduct & {
    price: string;
    originalPrice?: string;
    stock?: number;
  };
  onAddToFavorites?: () => void;
}

export function ProductDetailsModal({
  isOpen,
  onClose,
  product,
  onAddToFavorites,
}: ProductDetailsModalProps) {
  const [actualProduct, setActualProduct] = useState(product);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { refreshCart, cart } = useCart();
  const { currencyCode } = useCurrencyStore();
  const params = useParams();
  const router = useRouter();
  const { setOpenModal, province, municipality } = useLocationStore();
  const { customer } = useCustomer();
  const [open, setOpen] = useState(false);

  const selectedVariantId = actualProduct.variants?.[selectedVariant]?.id;
  const stock = actualProduct.variants?.[selectedVariant]?.inventory_quantity || 0;
  const addable = maxAddableQuantity(cart, selectedVariantId || "", stock);
  const addDisabled =
    !selectedVariantId || !canAddToCart(cart, selectedVariantId, stock, quantity);

  const { showToast } = useToastStore();

  const { data } = useReviews({ productId: product.id });
  const reviews = data?.reviews || [];
  const averageRating = data?.average_rating || 0;

  const { isFavorite, addFavorite, removeFavorite, favorites, isLoading } =
    useFavorites();

  const { mutate } = useMutation({
    mutationFn: () => {
      if (actualProduct?.variants?.[selectedVariant]?.id) {
        return subscribeToProduct({
          variant_id: actualProduct?.variants?.[selectedVariant]?.id,
          email: customer?.email ?? "",
        });
      }
      return Promise.reject();
    },
    onSuccess: () => showToast("Gracias por suscribirte.", "ok"),
    onError: () => showToast("Error al suscribirte al producto.", "err"),
    onSettled: () => setOpen(false),
  });

  // Filter out empty strings and invalid images
  // Images can be strings (URLs) or objects with a 'url' property
  const rawImages = product.images || [product.thumbnail];
  const validImages = rawImages
    .map((img) => {
      if (typeof img === "string") return img;
      if (img && typeof img === "object" && "url" in img)
        return img.url as string;
      return null;
    })
    .filter((img): img is string => Boolean(img && img.trim() !== ""));
  const finalImages =
    validImages.length > 0 ? validImages : ["/placeholder-product.png"];

  const handleIncrement = () => {
    if (quantity < addable) setQuantity(quantity + 1);
    if (quantity + 1 === addable && addable > 0) {
      showToast(`Lo sentimos, solo puedes añadir ${addable} unidad(es) más.`, "err", {
        action: {
          label: "Suscríbete a restock",
          onClick: () => mutate(),
        },
      });
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const handleReset = () => {
    setQuantity(1);
  };

  const handleVariantSelect = (index: number) => {
    setSelectedVariant(index);
    setQuantity(1);
    setActualProduct({
      ...product,
      price: `${getSymbol(currencyCode)}${(product?.variants?.[index]?.calculated_price?.calculated_amount ?? 0).toFixed(2)}`,
      originalPrice: `${getSymbol(currencyCode)}${(product?.variants?.[index]?.calculated_price?.original_amount ?? 0).toFixed(2)}`,
      stock: product?.variants?.[index]?.inventory_quantity ?? 0,
    });
  };

  const isFav = isFavorite(product.id);
  const [isProcessing, setIsProcessing] = useState(false);

  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isProcessing || isLoading) return;

    setIsProcessing(true);
    try {
      if (isFav) {
        // Find favorite ID
        const fav = favorites?.find(
          (f: StoreFavorite) => f.product_id === product.id
        );
        if (!fav) {
          console.warn(
            `Trying to remove favorite for product ${product.id} but no favorite entry found`
          );
          return;
        }
        if (fav) {
          await removeFavorite(fav.id);
        }
      } else {
        await addFavorite(product.id);
      }
    } catch (err: unknown) {
      // See favorite-button.tsx: the 401 bounce happens in the server action.
      console.error("❌ Error al actualizar favoritos:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-cream relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl p-6 shadow-2xl md:p-8">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 hover:text-gray-900"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Left: Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-square w-full overflow-hidden rounded-lg bg-white">
              <Image
                src={finalImages[selectedImage]}
                alt={actualProduct.title}
                width={400}
                height={400}
                className="h-full w-full object-contain"
              />
            </div>

            {/* Thumbnails - Carousel */}
            {finalImages.length > 1 && (
              <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-2">
                {finalImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square w-20 shrink-0 overflow-hidden rounded-lg border-2 ${
                      selectedImage === idx
                        ? "border-green-main"
                        : "border-transparent"
                    }`}
                  >
                    <Image
                      src={img}
                      alt={`${actualProduct.title} ${idx + 1}`}
                      width={80}
                      height={80}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Details */}
          <div className="flex flex-col gap-4">
            {/* Title */}
            <h2 className="font-inter text-2xl font-semibold text-gray-800">
              {actualProduct.title}
            </h2>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <ProductRating
                product_id={actualProduct.id}
                rating={averageRating}
                // readOnly
              />
              <span className="text-xs text-gray-500">
                ({reviews.length} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-inter text-orange-main text-3xl font-bold">
                {actualProduct.price}
              </span>
              {actualProduct.originalPrice &&
                actualProduct.originalPrice !== actualProduct.price && (
                  <span className="font-inter text-xl text-gray-400 line-through">
                    {actualProduct.originalPrice}
                  </span>
                )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-3">
              <QuantitySelector
                quantity={quantity}
                onIncrement={handleIncrement}
                onDecrement={handleDecrement}
                onReset={handleReset}
                max={Math.max(1, addable)}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                disabled={addDisabled}
                onClick={async () => {
                  if (!product.id) return;

                  if (!customer) {
                    showToast("Inicia sesión para añadir productos al carrito", "err");
                    router.push(`/${(params.countryCode as string) || "cu"}/login`);
                    return;
                  }

                  if (!selectedVariantId) {
                    showToast("Selecciona una variante válida.", "err");
                    return;
                  }

                  try {
                    await addToCart({
                      variantId: selectedVariantId,
                      quantity,
                      countryCode: (params.countryCode as string) || "cu",
                      currency_code: currencyCode.toLowerCase(),
                      province,
                      municipality,
                    });
                    await refreshCart();
                    onClose();
                    showToast("Producto añadido al carrito", "ok");
                  } catch (e: any) {
                    console.error(e);
                    showToast(e?.message || "Error al añadir al carrito", "err");
                  }
                }}
                className="bg-green-main flex-1 gap-2 py-6 text-base font-semibold hover:bg-[#047a2e] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ShoppingCart className="h-5 w-5" />
                {stock <= 0 ? "Sin stock" : "Añadir"}
              </Button>
              <Button
                onClick={toggle}
                className="border-green-main bg-green-main/10 text-green-main hover:bg-orange-main flex-1 gap-2 border-2 py-6 text-base font-semibold hover:text-white"
              >
                <Heart
                  className={cn("h-5 w-5", isFav ? "fill-green-main" : "")}
                />
                {isFav ? "Eliminar de favoritos" : "Añadir a favoritos"}
              </Button>
            </div>

            {/* Select Variants */}
            {(product?.variants?.length ?? 0) > 1 && (
              <div className="flex gap-2">
                {product.variants?.map((variant, index) => (
                  <Button
                    onClick={() => handleVariantSelect(index)}
                    variant="outline"
                    className={cn(
                      "bg-transparent text-xs",
                      selectedVariant === index &&
                        "border-green-main bg-orange-main/20"
                    )}
                    size="sm"
                    key={variant.id}
                  >
                    {variant.title}
                  </Button>
                ))}
              </div>
            )}

            {/* Stock Info */}
            <div className="bg-orange-main/10 flex items-center justify-between rounded-lg px-4 py-2">
              <p className="font-inter text-orange-main text-sm font-semibold">
                Stock: {stock} artículos disponibles
                {addable < stock && addable > 0
                  ? ` (puedes añadir ${addable} más)`
                  : ""}
              </p>

              {stock === 0 && (
                <Button variant="ghost" onClick={() => setOpen(true)}>
                  Suscribirse a stock
                </Button>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <p className="font-inter prose text-sm whitespace-pre-line text-gray-600">
                {product.description}
              </p>
            </div>

            {/* Shipping Info */}
            <div className="border-green-main flex items-center gap-3 rounded-lg border p-3">
              <div className="bg-green-main flex h-10 w-10 items-center justify-center rounded-lg">
                <Truck className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-inter text-green-main text-sm font-semibold">
                  Envío disponible
                </p>
                <p className="font-inter text-xs">Disponible</p>
              </div>
              <Button
                onClick={() => setOpenModal(true)}
                className="border-green-main bg-green-main/10 text-green-main hover:bg-orange-main hover:border-orange-main border-2 hover:text-white"
              >
                Cambiar zona
              </Button>
            </div>
            {/* Reviews */}
            {/* <div className="mt-4 border-t pt-4">
              <ReviewsList productId={product.id} />
            </div> */}
          </div>
        </div>
      </div>

      <SubscribeToStockModal
        handleAction={() => mutate()}
        handleClose={() => setOpen(false)}
        open={open}
      />
    </div>
  );
}
